import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { buildCasinoKpiMoneyTypeParam } from "../casino-kpis/casinoKpiFilters";

function getAuthenticatedInstance() {
  let token = null;
  try {
    token = JSON.parse(localStorage.getItem("token"));
  } catch {
    token = null;
  }
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

function omitEmptyParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  );
}

/**
 * API sort field names for GET /casino/analytics/providers (kept in sync with
 * `CASINO_PROVIDERS_SORT_FIELD_MAP` in `casinoProvidersColumns.tsx`).
 */
const SORT_FIELD_TO_API = {
  name: "name",
  uniquePlayers: "unique_players",
  rounds: "rounds",
  avgRounds: "avg_rounds",
  turnover: "turnover",
  wins: "wins",
  rtpPct: "rtp",
  ggr: "ggr",
  avgBet: "avg_bet",
};

/**
 * @param {string} field UI column key
 * @param {string | null | undefined} order Ant Table sort order
 * @returns {string | undefined} e.g. `-turnover` (desc) or `name` (asc)
 */
export function buildCasinoProvidersSortParam(field, order) {
  if (!field || !order) {
    return undefined;
  }
  const apiField = SORT_FIELD_TO_API[field];
  if (!apiField) {
    return undefined;
  }
  return order === "descend" ? `-${apiField}` : apiField;
}

/**
 * @param {Record<string, unknown>} raw
 * @param {number} [page=1]
 * @param {number} [index=0]
 * @returns {Object | null} Normalized table row, or null if invalid
 */
export function normalizeCasinoProviderItem(raw, page = 1, index = 0) {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const name = String(raw.name ?? "");
  const safeName = name.length > 0 ? name : "row";
  return {
    key: `provider-${page}-${index}-${safeName}`,
    name,
    totalGames: Number(raw.total_games) || 0,
    activeGames: Number(raw.active_games) || 0,
    uniquePlayers: Number(raw.unique_players) || 0,
    rounds: Number(raw.rounds) || 0,
    avgRounds: Number(raw.avg_rounds) || 0,
    turnover: Number(raw.turnover) || 0,
    wins: Number(raw.wins) || 0,
    rtpPct: Number(raw.rtp) || 0,
    ggr: Number(raw.ggr) || 0,
    avgBet: Number(raw.avg_bet) || 0,
  };
}

/**
 * Optional copy for the Ant table empty state when the API returns no rows
 * (same idea as `data.message` on sportsbook list endpoints).
 * @param {object} root Top-level response body
 * @param {object} inner `data` envelope (or `root` if flat)
 * @param {number} total
 * @returns {string | null}
 */
function pickCasinoProvidersEmptyText(root, inner, total) {
  if (total > 0) {
    return null;
  }
  if (inner && typeof inner === "object" && "message" in inner) {
    const m = inner.message;
    if (typeof m === "string" && m.trim().length > 0) {
      return m.trim();
    }
  }
  if (root && typeof root === "object" && "meta" in root && root.meta) {
    const m = root.meta;
    if (m && typeof m === "object") {
      if (typeof m.message === "string" && m.message.trim().length > 0) {
        return m.message.trim();
      }
      if (typeof m.empty_message === "string" && m.empty_message.trim().length > 0) {
        return m.empty_message.trim();
      }
    }
  }
  return null;
}

/**
 * @param {unknown} body
 * @returns {{
 *   rows: object[];
 *   total: number;
 *   page: number;
 *   size: number;
 *   pages: number;
 *   meta: unknown;
 *   emptyText: string | null;
 * }}
 */
export function normalizeCasinoProvidersResponse(body) {
  if (!body || typeof body !== "object") {
    return {
      rows: [],
      total: 0,
      page: 1,
      size: 0,
      pages: 0,
      meta: null,
      emptyText: null,
    };
  }
  const root = body;
  const inner =
    "data" in root && root.data && typeof root.data === "object"
      ? root.data
      : root;
  if (!inner || typeof inner !== "object" || !("items" in inner) || !Array.isArray(inner.items)) {
    return {
      rows: [],
      total: 0,
      page: 1,
      size: 0,
      pages: 0,
      meta: "meta" in root ? root.meta : null,
      emptyText: null,
    };
  }
  const items = inner.items;
  const pageNum = "page" in inner ? Number(inner.page) || 1 : 1;
  const rows = items
    .map((item, i) =>
      normalizeCasinoProviderItem(
        /** @type {Record<string, unknown>} */ (item),
        pageNum,
        i,
      ),
    )
    .filter(Boolean);
  const total = "total" in inner ? Number(inner.total) || 0 : 0;
  return {
    rows,
    total,
    page: pageNum,
    size: "size" in inner ? Number(inner.size) || 0 : 0,
    pages: "pages" in inner ? Number(inner.pages) || 0 : 0,
    meta: "meta" in root ? root.meta : null,
    emptyText: pickCasinoProvidersEmptyText(root, inner, total),
  };
}

/**
 * One page of GET /admin-panel-v2/casino/analytics/providers
 *
 * @param {Object} [params]
 * @param {string} [params.start_date] YYYY-MM-DD
 * @param {string} [params.end_date] YYYY-MM-DD
 * @param {number} [params.page=1] 1-based
 * @param {number} [params.size=20] page size
 * @param {string} [params.search] provider name search
 * @param {string} [params.sort] optional, e.g. `-turnover` (see `buildCasinoProvidersSortParam`). If the backend does not support `sort` yet, document in the PR and keep sending — harmless when ignored.
 * @param {string[]} [params.money_type] currency types
 */
export async function fetchCasinoProvidersReport(params = {}) {
  const start_date = params.start_date;
  const end_date = params.end_date;
  if (!start_date || !end_date) {
    return {
      rows: [],
      total: 0,
      page: 1,
      size: 20,
      pages: 0,
      meta: null,
      emptyText: null,
    };
  }

  const instance = getAuthenticatedInstance();
  const page = Number(params.page) > 0 ? Number(params.page) : 1;
  const size = Number(params.size) > 0 ? Number(params.size) : 20;
  const search =
    typeof params.search === "string" ? params.search.trim() : "";
  const sort =
    typeof params.sort === "string" && params.sort.trim() !== ""
      ? params.sort.trim()
      : undefined;
  const query = omitEmptyParams({
    start_date,
    end_date,
    page,
    size,
    ...(search ? { search } : {}),
    ...(sort ? { sort } : {}),
    ...buildCasinoKpiMoneyTypeParam(params.money_type),
  });

  const { data } = await instance.get(
    API_ROUTES.GET_CASINO_ANALYTICS_PROVIDERS,
    { params: query },
  );

  return normalizeCasinoProvidersResponse(data);
}
